'use client'

import { useMutation, useQuery } from '@apollo/client'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  DatePicker,
  Skeleton,
  TimePicker,
  Tooltip
} from '@chain-zen/ui'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { SHIPMENT_TRACKING_DATA, UPDATE_SHIPMENT_TRACKING_WINDOW } from '../../../../../data/queries/tracking'
import { TrackingEndedIndicator } from './tracking-ended-indicator'
import {
  combineDateTime,
  getTrackingWindowSummary,
  isTrackingWindowComplete,
  parseTrackingWindowDate
} from './tracking-window.utils'

interface ITrackingWindowProps {
  shipmentID: string
}

/**
 * TrackingWindow Component
 *
 * This component displays and allows editing of the tracking start and end times for a shipment.
 * It fetches the current tracking window from the backend using the shipmentID prop,
 * and provides date/time pickers for the user to update these values.
 * When both start and end times are provided, it triggers a mutation to save the updated values.
 *
 * Props:
 *   - shipmentID: string - The unique identifier for the shipment whose tracking window is being managed.
 */
export const TrackingWindow = ({ shipmentID }: ITrackingWindowProps) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const [showSavedIndicator, setShowSavedIndicator] = useState(false)

  // Local state for date/time fields
  const [startDate, setStartDate] = useState<Date | undefined>(undefined)
  const [startTime, setStartTime] = useState<string>('')
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const [endTime, setEndTime] = useState<string>('')

  /** Query existing tracking start/end times */
  const { loading } = useQuery<{
    shipmentTrackingData: { trackingEndTime: string | null; trackingStartTime: string | null }
  }>(SHIPMENT_TRACKING_DATA, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (!data?.shipmentTrackingData) return
      const { trackingStartTime, trackingEndTime } = data.shipmentTrackingData

      const parsedStartDate = parseTrackingWindowDate(trackingStartTime, userTimezone)
      const parsedEndDate = parseTrackingWindowDate(trackingEndTime, userTimezone)

      if (parsedStartDate) {
        const zonedDate = parsedStartDate
        setStartDate(zonedDate)
        setStartTime(format(zonedDate, 'HH:mm'))
      } else {
        setStartDate(undefined)
        setStartTime('')
      }

      if (parsedEndDate) {
        const zonedDate = parsedEndDate
        setEndDate(zonedDate)
        setEndTime(format(zonedDate, 'HH:mm'))
      } else {
        setEndDate(undefined)
        setEndTime('')
      }
    },
    variables: { shipmentID }
  })

  /** Mutation */
  const [saveTrackingTimes] = useMutation(UPDATE_SHIPMENT_TRACKING_WINDOW, {
    onCompleted: () => {
      setShowSavedIndicator(true)
    },
    onError: (error) => {
      const message = error?.message || 'Failed to update tracking times'
      toast.error(message)
    }
  })

  // Function to save tracking times when all fields are complete
  const saveTrackingWindow = (
    startDate: Date | undefined,
    startTime: string,
    endDate: Date | undefined,
    endTime: string
  ) => {
    if (!isTrackingWindowComplete(startDate, startTime, endDate, endTime)) return

    const startLocalISO = combineDateTime(startDate!, startTime, userTimezone)
    const endLocalISO = combineDateTime(endDate!, endTime, userTimezone)

    if (!startLocalISO || !endLocalISO) return

    saveTrackingTimes({
      variables: {
        input: {
          shipmentID,
          timezone: userTimezone,
          trackingEndTime: endLocalISO,
          trackingStartTime: startLocalISO
        }
      }
    })
  }

  // Enhanced handlers that trigger save when complete
  const handleStartDateChange = (date: Date | undefined) => {
    setStartDate(date)
    // Only save if all fields will be complete after this change
    if (date && startTime && endDate && endTime) {
      saveTrackingWindow(date, startTime, endDate, endTime)
    }
  }

  const handleStartTimeChange = (time: string) => {
    setStartTime(time)
    // Only save if all fields will be complete after this change
    if (startDate && time && endDate && endTime) {
      saveTrackingWindow(startDate, time, endDate, endTime)
    }
  }

  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date)
    // Only save if all fields will be complete after this change
    if (startDate && startTime && date && endTime) {
      saveTrackingWindow(startDate, startTime, date, endTime)
    }
  }

  const handleEndTimeChange = (time: string) => {
    setEndTime(time)
    // Only save if all fields will be complete after this change
    if (startDate && startTime && endDate && time) {
      saveTrackingWindow(startDate, startTime, endDate, time)
    }
  }

  // Hide the saved indicator automatically after 1s - this useEffect is still necessary
  useEffect(() => {
    if (!showSavedIndicator) return
    const timer = setTimeout(() => setShowSavedIndicator(false), 1000)
    return () => clearTimeout(timer)
  }, [showSavedIndicator])

  if (loading) {
    return <Skeleton className="w-full h-10 rounded-md" />
  }

  const trackingWindowSummary = getTrackingWindowSummary({
    endDate,
    endTime,
    startDate,
    startTime
  })

  return (
    <Accordion type="single" collapsible={true} className="w-full border rounded-md p-2">
      <AccordionItem value="tracking-window" className="border-none">
        <AccordionTrigger className="hover:no-underline p-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-left">
            <Tooltip
              content={`Tracking window defines the period during which tracking data is collected and displayed. Tracking start and end times are shown in (${userTimezone}) timezone.`}
            >
              <span className="text-sm font-medium cursor-help">Tracking Window</span>
            </Tooltip>
            {!!trackingWindowSummary && <span className="text-xs text-muted-foreground">{trackingWindowSummary}</span>}
            {!!showSavedIndicator && <span className="text-sm text-green-600 dark:text-green-400">Saved</span>}
            <TrackingEndedIndicator endDate={endDate} endTime={endTime} timezone={userTimezone} />
          </div>
        </AccordionTrigger>
        <AccordionContent className="overflow-x-auto py-2">
          <div className="flex flex-col gap-4 items-start">
            <div className="space-y-2 w-full">
              <span className="text-sm font-medium text-muted-foreground">From</span>
              <div className="flex flex-col sm:flex-row gap-2 w-full">
                <div className="w-full sm:w-auto">
                  <DatePicker date={startDate} onSelect={handleStartDateChange} placeholder="Start date" />
                </div>
                {!!startDate && (
                  <div className="w-full sm:w-auto">
                    <TimePicker
                      time={startTime}
                      onSelect={handleStartTimeChange}
                      label="Start time"
                      name="tracking-start-time"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 w-full">
              <span className="text-sm font-medium text-muted-foreground">To</span>
              <div className="flex flex-col sm:flex-row gap-2 w-full">
                <div className="w-full sm:w-auto">
                  <DatePicker date={endDate} onSelect={handleEndDateChange} placeholder="End date" />
                </div>
                {!!endDate && (
                  <div className="w-full sm:w-auto">
                    <TimePicker
                      time={endTime}
                      onSelect={handleEndTimeChange}
                      label="End time"
                      name="tracking-end-time"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
